import type { ExpenditureCategory, ExpenditureDto, TriageItem } from '@signalvote/shared';
import { BankTransactionAttribution } from '@signalvote/shared';
import type { Edit } from '@signalvote/data';
import { edit, every } from '@/lib/data';
import { useSortInbox } from './useSortInbox';

/**
 * A write's edit to the matched row(s) in every cached display ledger, for its `patch` (on click,
 * rolled back by the write door on a failure) or its `land` (from the response). The NeedsAction
 * feed is left to the server's write set, which refreshes both inbox reads.
 */
export function editInboxRow(matches: (item: TriageItem) => boolean, change: (item: TriageItem) => TriageItem): Edit {
  return edit(useSortInbox, every, (data) => ({
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (matches(item) ? change(item) : item)),
    })),
  }));
}

// ────────────────────────────────────────────────────────────────────────────
// Patches — each mirrors the resolved projection the sort-inbox refetch produces
// (`LedgerStatusProjection.DeriveStatus`), so the write-set refetch
// is a visual no-op (no flicker). They set only the row's own status stamp
// (`status` / `actionNeeded` / `resolvedAs`, plus the entity's stamped
// `ledgerStatus` / bank `attribution` for internal coherence) — never the
// server-computed verdict (limit warnings, totals, "needs info", closeability),
// which stays server-truth-driven.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Confirm-due → Done (Pending/born-Pending → Confirmed). `status: 'Done'` routes
 * the Action column to the filled green check, `resolvedAs: 'Confirmed'` labels it,
 * and `actionNeeded: null` clears the NeedsAction dispatch. An Expenditure row's
 * slim DTO flips `ledgerStatus` Pending → Confirmed so the snapshot stays coherent
 * for any immediately-following interaction.
 */
export function markConfirmed(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Confirmed',
      expenditure: { ...item.expenditure, ledgerStatus: 'Confirmed' },
    };
  }
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Confirmed' };
}

/**
 * Done → NeedsAction/Confirm (Confirmed/Voided → Pending). The inverse of
 * {@link markConfirmed}; serves BOTH unconfirm (uncheck a Done money row) and
 * unvoid (restore a Voided one) — each returns the row to the Pending review
 * queue, where the projection reads NeedsAction/Confirm for a complete row.
 *
 * For UNCONFIRM this is always exact: a Done row is complete by construction
 * (`DeriveStatus` only reaches Done when `IsComplete`), so a Pending complete row
 * reads NeedsAction/Confirm. For UNVOID the client can't see completeness on the
 * slim DTO, so a restored row that's genuinely missing donor-info / a payee
 * address would settle to its `ProvideDonorInfo` / `ProvideExpenditureInfo` flag
 * instead of Confirm — that rarer case is corrected by the write-set
 * refetch (the common complete-row case is exact).
 */
export function markPendingReview(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'NeedsAction',
      actionNeeded: 'Confirm',
      resolvedAs: null,
      expenditure: { ...item.expenditure, ledgerStatus: 'Pending' },
    };
  }
  if (item.kind === 'Contribution') {
    return {
      ...item,
      status: 'NeedsAction',
      actionNeeded: 'Confirm',
      resolvedAs: null,
      contribution: { ...item.contribution, ledgerStatus: 'Pending' },
    };
  }
  return item;
}

/**
 * A money row (Contribution / Expenditure) excluded from the report → terminal
 * "Not reported" (Done / `resolvedAs: 'Voided'`, ADR-016). The dimmed ⊘ replaces
 * the green check; the stamped `ledgerStatus` flips to Voided.
 */
export function markVoided(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Voided',
      expenditure: { ...item.expenditure, ledgerStatus: 'Voided' },
    };
  }
  if (item.kind === 'Contribution') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Voided',
      contribution: { ...item.contribution, ledgerStatus: 'Voided' },
    };
  }
  return item;
}

/**
 * Mark-as-reported (Confirmed → Reported). ONLY the stamped `ledgerStatus` flips:
 * in an OPEN period a Reported-status row still RENDERS Done (ADR-019 D6), so the
 * projection keeps `status: 'Done'` and the kind's resolution word — the refetch
 * changes nothing but `ledgerStatus`. Preserving the render triple (spreading it
 * unchanged) is what mirrors the projection and avoids a flicker.
 */
export function markReported(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return { ...item, expenditure: { ...item.expenditure, ledgerStatus: 'Reported' } };
  }
  if (item.kind === 'Contribution') {
    return { ...item, contribution: { ...item.contribution, ledgerStatus: 'Reported' } };
  }
  return item;
}

/** Reverse of {@link markReported} — unmark (Reported → Confirmed). */
export function markUnreported(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return { ...item, expenditure: { ...item.expenditure, ledgerStatus: 'Confirmed' } };
  }
  if (item.kind === 'Contribution') {
    return { ...item, contribution: { ...item.contribution, ledgerStatus: 'Confirmed' } };
  }
  return item;
}

/**
 * Bank line excluded from the report (Unattributed → ExcludedFromReport, ADR-014):
 * → Done / `resolvedAs: 'Excluded'` (treasurer copy "Not reported"). The slim
 * bank DTO's `attribution` flips to keep the snapshot coherent.
 */
export function markBankExcluded(item: TriageItem): TriageItem {
  if (item.kind !== 'BankLine') return item;
  return {
    ...item,
    status: 'Done',
    actionNeeded: null,
    resolvedAs: 'Excluded',
    bt: { ...item.bt, attribution: BankTransactionAttribution.ExcludedFromReport },
  };
}

/**
 * Bank line reopened (ExcludedFromReport / DepositOfRecordedChecks → Unattributed):
 * → NeedsAction/Classify, back in the un-triaged queue. The inverse of
 * {@link markBankExcluded}.
 */
export function markBankReopened(item: TriageItem): TriageItem {
  if (item.kind !== 'BankLine') return item;
  return {
    ...item,
    status: 'NeedsAction',
    actionNeeded: 'Classify',
    resolvedAs: null,
    bt: { ...item.bt, attribution: BankTransactionAttribution.Unattributed },
  };
}

/** Orphan refund linked to its parent contribution → Done / `resolvedAs: 'Linked'`. */
export function markRefundLinked(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Linked' };
}

/** Orphan refund set aside as standalone → Done / `resolvedAs: 'Standalone'`. */
export function markRefundStandalone(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Standalone' };
}

/**
 * Refund returned to orphan (unlink a Linked refund / revoke a Standalone one):
 * → NeedsAction/MatchRefund. Both uncheck paths land on the same resolved shape.
 */
export function markRefundOrphan(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'NeedsAction', actionNeeded: 'MatchRefund', resolvedAs: null };
}

// ────────────────────────────────────────────────────────────────────────────
// Field patch (NOT a status mark) — a data-only re-write of a single displayed
// field. Unlike the `mark*` patches above, this leaves the render triple
// (`status` / `actionNeeded` / `resolvedAs`) ALONE: any derived consequence (e.g.
// a `CategorizeExpenditure` prompt clearing) stays server-truth-driven and lands
// on the write-set refetch.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Re-category an Expenditure row — the inline Category re-pick on the close ledger
 * (root cause #3). The category `<select>` is controlled straight off this cache
 * with no draft state, so without an optimistic write a successful save keeps
 * showing the OLD category (visibly snapping back) until the full sort-inbox
 * refetch commits — a display-correctness problem on a compliance screen. This
 * factory sets ONLY the row's own `expenditure.category`, keeping the server the
 * source of truth for every derived value. A no-op for non-Expenditure rows.
 */
export function patchExpenditureCategory(
  category: ExpenditureCategory,
): (item: TriageItem) => TriageItem {
  return (item) =>
    item.kind === 'Expenditure'
      ? { ...item, expenditure: { ...item.expenditure, category } }
      : item;
}

/**
 * Write a saved Expenditure's dates into its row. The date cells read straight off
 * this cache, so without it a save snaps back until the refetch lands.
 */
export function patchExpenditureSavedDates(
  saved: Pick<ExpenditureDto, 'date' | 'authorizedDate' | 'incurredDate'>,
): (item: TriageItem) => TriageItem {
  return (item) =>
    item.kind === 'Expenditure'
      ? {
          ...item,
          date: saved.date,
          expenditure: {
            ...item.expenditure,
            date: saved.date,
            authorizedDate: saved.authorizedDate,
            incurredDate: saved.incurredDate,
          },
        }
      : item;
}

/**
 * Write a bank line's freshly-bumped token into its row, from a write's response. A bank line's
 * delete is not a row write yet and reads its token from the cached row, and a rapid follow-up's
 * patch cancels the write-set refetch that would bring it.
 */
export function withBankLineVersion(version: number | null | undefined): (item: TriageItem) => TriageItem {
  return (item) =>
    item.kind === 'BankLine' && version != null ? { ...item, bt: { ...item.bt, concurrencyVersion: version } } : item;
}
